<script setup lang="ts">
import { getSiteDetail, type SiteResourceDetailed } from "@carnet/api/backoffice";

const props = defineProps<{
  siteId: number | string;
  selectable?: boolean;
  selectedSiteId?: number | string | null;
}>();

const emit = defineEmits<{
  close: [];
  pick: [site: SiteResourceDetailed];
}>();

const { t } = useI18n();

const isChosen = computed(() => props.selectedSiteId != null && String(props.selectedSiteId) === String(props.siteId));

const site = ref<Awaited<ReturnType<typeof getSiteDetail<"$fetch">>>>();
const loading = ref(true);

async function load() {
  loading.value = true;
  try {
    site.value = await getSiteDetail({
      composable: "$fetch",
      path: { locale: appLocale(), id: String(props.siteId) },
      headers: { Accept: "application/json" },
    });
  } catch {
    site.value = undefined;
  } finally {
    loading.value = false;
  }
}

watch(
  () => props.siteId,
  () => load(),
  { immediate: true }
);

const detail = computed(() => site.value?.data);
</script>

<template>
  <WebMapPopup :loading="loading" @close="emit('close')">
    <template #loading>
      <AppTileSiteSkeleton />
    </template>
    <template v-if="detail">
      <AppTileSite :site="detail" :to="selectable ? undefined : $localePath({ name: 'sites.detail', params: { id: detail.id } })">
        <UButton
          v-if="selectable"
          :color="isChosen ? 'primary' : 'secondary'"
          :variant="isChosen ? 'soft' : 'solid'"
          :trailing-icon="isChosen ? 'icon:check' : undefined"
          class="self-start"
          :aria-label="`${t(isChosen ? 'site.panel.chosen' : 'site.panel.pick')} ${detail.name}`"
          @click="emit('pick', detail)"
        >
          {{ isChosen ? t("site.panel.chosen") : t("site.panel.pick") }}
        </UButton>
      </AppTileSite>
    </template>
  </WebMapPopup>
</template>
